'use client';

/**
 * InsightsPanel — the new "Insights" rail surface.
 *
 * Phase 1.1 of the right-rail UX direction. Vertical scroll of
 * sub-cards. Mounted alongside CopilotPanel inside the right rail
 * column; visibility toggled via `useRailSurfaceStore.active`.
 * Always mounted (when the rail is visible at all) so React Query
 * keeps each sub-card's data warm and switching surfaces is a CSS
 * toggle, not a fetch.
 *
 * Card list is **matrix-driven** — same single-source-of-truth
 * pattern Copilot tabs use. The total-matrix declares
 * `insights: InsightsCardConfig[]` per `(mode, selection)`; this
 * panel reads that list and renders the corresponding components
 * via `INSIGHTS_CARD_REGISTRY`. Adding / removing a card =
 * matrix change + registry entry, not chrome-code change.
 *
 * Per-platform visibility honoured via `card.platforms` (mirrors
 * `TotalTabConfig.platforms`).
 *
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 * @see klyde_edge/ui_config/total_matrix.py — INSIGHTS_CARDS
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain copilot
 */

import { useMemo } from 'react';
import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useIsMobile } from '@/lib/hooks/use-is-mobile';
import { CardChromeProvider } from '@/lib/design/card-chrome';
import { CardPlacementProvider } from '@/lib/design/card-export';
import { INSIGHTS_CARD_REGISTRY } from './card-registry';
import { RailContextHeader } from './rail-context-header';
import { CollapsibleCard } from './collapsible-card';

export function InsightsPanel() {
  const { mode, contentSelection: selection } = useViewportSelection();
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  // `getViewportConfig` is a stable store-method ref — subscribe to
  // `matrix` and key the memo on it, else `visibleCards` caches the
  // first-render null (matrix still in flight) and the rail stays empty.
  const matrix = useTotalMatrixStore((s) => s.matrix);
  const { isMobile } = useIsMobile();
  const currentPlatform = isMobile ? 'mobile' : 'desktop';

  const visibleCards = useMemo(() => {
    const cfg = getViewportConfig(mode, selection);
    const cards = cfg?.insights ?? [];
    const byPlatform = cards.filter((c) => {
      // Missing/empty platforms list = render everywhere; explicit
      // list = filter by membership. Same rule the Copilot tab strip
      // uses for `tab.platforms`.
      const platforms = c.platforms;
      if (!platforms || platforms.length === 0) return true;
      return platforms.includes(currentPlatform);
    });
    // Cards that are always about whatever's currently selected move to
    // the front as a group, keeping their own relative order; everything
    // else keeps its declared order, shifted down. `.filter()` is stable,
    // so this is a plain two-bucket partition, not a sort. See
    // klyde_ipe/docs/insights-rail-scope-ordering-plan.md.
    const promoted = byPlatform.filter((c) => c.follows_selection);
    const rest = byPlatform.filter((c) => !c.follows_selection);
    return [...promoted, ...rest];
  }, [mode, selection, getViewportConfig, currentPlatform, matrix]);

  return (
    <div className="h-full overflow-y-auto bg-panel">
      <div className="p-2 space-y-2">
        <RailContextHeader />
        {visibleCards.map((c) => {
          const Component = INSIGHTS_CARD_REGISTRY[c.key];
          if (!Component) {
            // Unknown card key from the matrix — log and skip rather
            // than throw. Forward-compat with backends that ship new
            // card keys before the frontend has the component.
            if (typeof window !== 'undefined') {
              // eslint-disable-next-line no-console
              console.warn(`[InsightsPanel] Unknown card key '${c.key}' — add to INSIGHTS_CARD_REGISTRY`);
            }
            return null;
          }
          // Wrap each card in CardChromeProvider so the card body
          // can read its label + icon (and typography) from context
          // via `useCardChrome()` instead of hardcoding them. Mirrors
          // the LayoutEngine mount path used by canvas cards.
          return (
            <CardChromeProvider
              key={c.key}
              contentKey={c.key}
              label={c.title}
              iconName={c.icon ?? null}
              strategy={c.strategy ?? null}
            >
              <CardPlacementProvider
                value={{ surface: 'rail', slot_id: c.key }}
              >
                <CollapsibleCard cardKey={c.key}>
                  <Component />
                </CollapsibleCard>
              </CardPlacementProvider>
            </CardChromeProvider>
          );
        })}
      </div>
    </div>
  );
}
